import {
    Button,
    ButtonIcon,
    ButtonText,
    EmptyState,
    Text,
    VStack
} from "@hosttools/frontend/components";
import type {AccountChannel, AccountType} from "@hosttools/frontend/models/account";
import {AddIcon} from "@hosttools/frontend/react/components/Icons";
import useChannel from "@hosttools/frontend/react/hooks/useChannel";
import type React from "react";
import {memo, useCallback} from "react";

import BadAccountItem from "./BadAccountItem";
import ListingList from "./ListingList";

import useAccountsByType from "@/client/hooks/useAccountsByType";

type Props = {
    accountType: AccountType;
    accountListings: Map<string, string[]>;
    multiSelect?: boolean;
    onAddAdditionalAccount: () => void;
    onReconnectAccount: (account: AccountChannel) => void;
    onChangeAccountListings: (accountListings: Map<string, string[]>) => void;
};

const AccountList: React.FC<Props> = ({
    multiSelect,
    accountType,
    accountListings,
    onReconnectAccount,
    onAddAdditionalAccount,
    onChangeAccountListings
}) => {
    const {accounts, badAccounts} = useAccountsByType(accountType);
    const {label} = useChannel(accountType);

    const handleChangeListListings = useCallback(
        (accountID: string, checkedListings: string[]) => {
            const next = new Map([...accountListings]);
            if (checkedListings.length) {
                next.set(accountID, checkedListings);
            } else {
                next.delete(accountID);
            }
            onChangeAccountListings(next);
        },
        [accountListings, onChangeAccountListings]
    );

    if (badAccounts.length === 0 && accounts.length === 0) {
        return (
            <EmptyState primaryText={`Let's connect your first ${label} account`}>
                <Button testID="btn-connect-account" onPress={onAddAdditionalAccount}>
                    <ButtonIcon as={AddIcon} />
                    <ButtonText>Connect account</ButtonText>
                </Button>
            </EmptyState>
        );
    }

    return (
        <VStack space="lg">
            {badAccounts.map(account => (
                <BadAccountItem
                    key={account._id}
                    account={account}
                    onReconnect={onReconnectAccount}
                />
            ))}
            {accounts.map(account => (
                <VStack key={account._id} space="md">
                    <Text color="blueGray.800" variant="md">
                        {account.username}
                    </Text>
                    <ListingList
                        accountID={account._id}
                        checkedListings={accountListings.get(account._id)}
                        multiSelect={multiSelect}
                        onChange={handleChangeListListings}
                    />
                </VStack>
            ))}
        </VStack>
    );
};

export default memo(AccountList);
